/**
 * Brand Lift Survey metric catalogue (BLS-40, BLS-41, BLS-53).
 *
 * Standard questions and answers come from the translation catalogue.
 * The action-wording dropdown stays in English and selects that question row.
 * Consideration answers and Custom copy are typed by the client.
 */

import { actionWordsFor, englishAnswerList, englishQuestion, fillSurveyBrand } from './catalogue.js';

const LEGACY_ANSWER_SETS = {
  adRecall: [
    ['Yes, I recall seeing it', "No, I don't recall seeing it", 'Not sure'],
  ],
  favourability: [
    ['Very favorable', 'Somewhat favorable', 'Neutral', 'Somewhat unfavorable', 'Very unfavorable'],
    ['Favorable', 'Neutral', 'Unfavorable'],
  ],
  actionIntent: [
    ['Very likely', 'Somewhat likely', 'Neither likely nor unlikely', 'Somewhat unlikely', 'Very unlikely'],
    ['Likely', 'Neutral', 'Unlikely'],
  ],
};

export const BRAND_METRICS = [
  {
    id: 'adRecall',
    label: 'Ad Recall',
    actionWords: actionWordsFor('adRecall'),
    answers: englishAnswerList('adRecall', 'default'),
  },
  {
    id: 'awareness',
    label: 'Awareness',
    actionWords: actionWordsFor('awareness'),
    answers: englishAnswerList('awareness', 'default'),
  },
  {
    id: 'consideration',
    label: 'Consideration',
    actionWords: actionWordsFor('consideration'),
    answers: ['[BRAND]', 'Brand 2', 'Brand 3', 'Brand 4'],
  },
  {
    id: 'favourability',
    label: 'Favourability',
    actionWords: actionWordsFor('favourability'),
    answers: englishAnswerList('favourability', '5'),
    answers3: englishAnswerList('favourability', '3'),
  },
  {
    id: 'actionIntent',
    label: 'Intent',
    actionWords: actionWordsFor('actionIntent'),
    answers: englishAnswerList('actionIntent', 'default'),
  },
  {
    id: 'custom',
    label: 'Custom',
    actionWords: [],
    answers: [],
  },
];

export const METRIC_WORDING_GUIDES = {
  adRecall: {
    col2Header: 'Intent / engagement level',
    rows: [
      { word: 'Seeing', col2: 'Passive / involuntary', context: 'The ad appeared while the user was browsing or scrolling. Measures basic ad recall.', bestFor: 'Display, image, banner, sponsored post' },
      { word: 'Watching', col2: 'Active / sustained', context: 'The user consumed the ad over several seconds.', bestFor: 'Video, in-stream, Stories, Reels' },
      { word: 'Encountering', col2: 'Accidental / unplanned', context: 'The user came across the ad as part of their content experience.', bestFor: 'Interstitial, native, high-impact' },
      { word: 'Viewing', col2: 'Intentional', context: 'The user deliberately looked at or inspected the ad.', bestFor: 'Expandable, carousel, rich media' },
      { word: 'Reading', col2: 'Cognitive / focused', context: 'The user focused on written copy or information within the ad.', bestFor: 'Text-heavy display, native, sponsored content' },
      { word: 'Interacting with', col2: 'Active / participatory', context: 'The user actively engaged with the ad beyond simply looking at it.', bestFor: 'Interactive, playable, rich media, AR' },
    ],
  },
  awareness: {
    col2Header: 'Awareness level',
    rows: [
      { word: 'Heard of', col2: 'General awareness', context: "Measures whether the brand exists in the respondent's awareness at all.", bestFor: 'General brand awareness; recommended default' },
      { word: 'Seen', col2: 'Visual awareness', context: 'Measures whether the respondent remembers visually encountering the brand.', bestFor: 'Visual brands, display-heavy campaigns' },
      { word: 'Come across', col2: 'Incidental awareness', context: 'Measures whether the respondent has encountered the brand somewhere previously.', bestFor: 'Broad digital, social, native' },
      { word: 'Read about', col2: 'Written awareness', context: 'Measures whether the respondent remembers reading about the brand.', bestFor: 'Content, editorial, native' },
      { word: 'Used', col2: 'Experience awareness', context: 'Measures whether the respondent has used the brand.', bestFor: 'Product and service campaigns' },
    ],
  },
  consideration: {
    col2Header: 'Intent level',
    rows: [
      { word: 'Consider buying from', col2: 'Commercial consideration', context: 'The respondent would consider purchasing from the brand.', bestFor: 'Retail, e-commerce, products' },
      { word: 'Consider purchasing from', col2: 'Purchase consideration', context: 'The respondent would consider making a purchase from the brand.', bestFor: 'Retail, e-commerce, products' },
      { word: 'Consider using', col2: 'Usage consideration', context: 'The respondent would consider using the product or service.', bestFor: 'Apps, platforms, utilities, services' },
      { word: 'Consider choosing', col2: 'General consideration', context: 'The respondent would consider selecting the brand over alternatives.', bestFor: 'Universal / category-neutral' },
      { word: 'Be open to buying from', col2: 'Openness to buy', context: 'The respondent would be open to buying from the brand.', bestFor: 'Retail, e-commerce, new customers' },
    ],
  },
  favourability: {
    col2Header: 'What it measures',
    rows: [
      { word: 'Opinion', col2: 'Overall evaluation', context: 'General positive or negative evaluation of the brand.', bestFor: 'Universal; recommended default' },
      { word: 'Impression', col2: 'General impression', context: 'The overall impression the brand has created.', bestFor: 'Brand campaigns, new positioning' },
      { word: 'View', col2: 'Considered evaluation', context: 'How the respondent currently views the brand.', bestFor: 'Corporate, services, established brands' },
      { word: 'Perception', col2: 'Brand perception', context: 'How the respondent mentally perceives the brand.', bestFor: 'Positioning, reputation, brand-image campaigns' },
    ],
  },
  actionIntent: {
    col2Header: 'Intent level',
    rows: [
      { word: 'Learn more about', col2: 'Exploratory intent', context: 'Measures intention to seek more information about the brand.', bestFor: 'Awareness and upper-funnel campaigns' },
      { word: 'Buy from', col2: 'Purchase intent', context: 'Measures intention to purchase a product or service.', bestFor: 'Retail, e-commerce, FMCG' },
      { word: 'Visit', col2: 'Traffic intent', context: 'Measures intention to actively visit the brand.', bestFor: 'Website traffic campaigns' },
      { word: 'Try', col2: 'Trial intent', context: 'Measures intention to try the brand or product.', bestFor: 'FMCG, apps, new products, services' },
      { word: 'Sign up with', col2: 'Conversion intent', context: 'Measures intention to register, join or create an account.', bestFor: 'SaaS, memberships, platforms' },
      { word: 'Recommend', col2: 'Advocacy intent', context: 'Measures intention to recommend the brand to someone else.', bestFor: 'Loyalty, referral, brand campaigns' },
    ],
  },
};

export function getMetricById(metricId) {
  return BRAND_METRICS.find((metric) => metric.id === metricId) || null;
}

export function buildQuestion(metric, actionWord, brand) {
  if (!metric) return '';
  const sentence = englishQuestion(metric.id, actionWord);
  if (sentence) return fillBrandPlaceholder(sentence, brand);
  return '';
}

export function fillBrandPlaceholder(value, brand) {
  if (value == null) return '';
  return fillSurveyBrand(value, brand);
}

export function defaultAnswersForMetric(metric, answerCount, brand) {
  if (!metric) return [''];
  const source =
    answerCount === 3 && metric.answers3
      ? metric.answers3
      : metric.answers || [];
  if (!source.length) return [''];
  return source.map((answer) => fillBrandPlaceholder(answer, brand));
}

function normalizeAnswer(value) {
  return String(value || '')
    .replace(/[’‘]/g, "'")
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

function sameAnswerList(left, right) {
  if (!left?.length || left.length !== right.length) return false;
  return left.every((answer, index) => normalizeAnswer(answer) === normalizeAnswer(right[index]));
}

/**
 * Keep client-typed answers. Replace the previous English defaults with the
 * workbook copy so translations can apply after a survey is re-saved.
 */
export function reconcileSavedAnswers(metric, parsedAnswers, brand, preferredCount) {
  const parsed = (parsedAnswers || []).map((answer) => String(answer || '').trim()).filter(Boolean);
  if (!metric || metric.id === 'custom' || metric.id === 'consideration') {
    return {
      answerCount: parsed.length || 2,
      answerOptions: parsed.length ? parsed : [''],
    };
  }
  const catalogueCount = metric.answers3 && parsed.length === 3 ? 3 : (metric.answers?.length || parsed.length || 5);
  const catalogueAnswers = defaultAnswersForMetric(metric, catalogueCount, brand);
  if (parsed.length && sameAnswerList(parsed, catalogueAnswers)) {
    return { answerCount: catalogueCount, answerOptions: parsed };
  }
  const legacy = LEGACY_ANSWER_SETS[metric.id] || [];
  if (parsed.length && legacy.some((set) => sameAnswerList(parsed, set))) {
    const count = metric.id === 'actionIntent' ? metric.answers.length : catalogueCount;
    return {
      answerCount: count,
      answerOptions: defaultAnswersForMetric(metric, count, brand),
    };
  }
  if (!parsed.length) {
    const count = metric.answers3 && Number(preferredCount) === 3
      ? 3
      : (metric.answers3 ? 5 : (metric.answers.length || 2));
    return { answerCount: count, answerOptions: defaultAnswersForMetric(metric, count, brand) };
  }
  return { answerCount: parsed.length, answerOptions: parsed };
}

export function inferActionWord(metric, questionWording) {
  const words = [...(metric?.actionWords || [])].sort((a, b) => b.length - a.length);
  const question = String(questionWording || '').toLowerCase();
  return words.find((word) => question.includes(String(word).toLowerCase())) || metric?.actionWords?.[0] || '';
}
